"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import NextLink from "next/link";
import { useTranslation } from "react-i18next";

const AGENT_IMAGES = [
  "https://getgeek-website-assets.b-cdn.net/ScoutCircle.webp",
  "https://getgeek-website-assets.b-cdn.net/SageCircle.webp",
  "https://getgeek-website-assets.b-cdn.net/AtlasCircle.webp",
  "https://getgeek-website-assets.b-cdn.net/CompassCircle.webp",
  "https://getgeek-website-assets.b-cdn.net/OmniLogoNew.webp",
];

const AGENT_ROLE_COLORS = [
  "#A020E8", // Scout
  "#E0457B", // Sage
  "#1D8FDC", // Atlas
  "#D4790B", // Compass
  "#E2622F", // Omni
];

// Same order as AGENT_IMAGES / the JSON "agents" array.
const AGENT_HREFS = [
  "/ai-agents/scout",
  "/ai-agents/sage",
  "/ai-agents/atlas",
  "/ai-agents/compass",
  "/ai-agents/omni",
];

const ZaynAiTeamSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const agents = t("zaynAiTeam.agents", { returnObjects: true }) || [];

  return (
    <Box
      sx={{
        py: { xs: 5, sm: 6, md: 8 },
        bgcolor: "#F7F4FD",
        direction: isArabic ? "rtl" : "ltr",
        mt: { xs: "14%", sm: "13%", md: "4%" },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            fontSize: { xs: "11px", sm: "12px" },
            fontWeight: 600,
            color: "#7A2BF5",
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontFamily: "var(--font-outfit)",
            textAlign: "center",
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          {t("zaynAiTeam.label")}
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "22px", sm: "26px", md: "30px" },
            fontWeight: 700,
            lineHeight: 1.3,
            color: "#14122B",
            fontFamily: "var(--font-plusjakarta)",
            textAlign: "center",
            mb: { xs: 1, sm: 1 },
          }}
        >
          {t("zaynAiTeam.title")}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "13px", sm: "14px" },
            color: "#55556E",
            lineHeight: 1.8,
            fontWeight: 400,
            fontFamily: "var(--font-outfit)",
            textAlign: "center",
            maxWidth: 620,
            mx: "auto",
            mb: { xs: 4, sm: 5 },
          }}
        >
          {t("zaynAiTeam.description")}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {agents.map((agent, index) => (
            <NextLink
              key={index}
              href={AGENT_HREFS[index] || "/ai-agents"}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Box
                sx={{
                  height: "100%",
                  bgcolor: "#FFFFFF",
                  borderRadius: "16px",
                  p: { xs: 2.5, sm: 3 },
                  textAlign: isArabic ? "right" : "left",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 10px 28px rgba(122,43,245,0.12)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    overflow: "hidden",
                    mb: { xs: 2, sm: 2.5 },
                  }}
                >
                  <img
                    src={AGENT_IMAGES[index]}
                    alt={agent.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: "15px", sm: "16px" },
                    fontWeight: 700,
                    color: "#14122B",
                    fontFamily: "var(--font-plusjakarta)",
                    mb: 0.5,
                  }}
                >
                  {agent.name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "11px", sm: "12px" },
                    fontWeight: 600,
                    color: AGENT_ROLE_COLORS[index] || "#7A2BF5",
                    fontFamily: "var(--font-outfit)",
                    mb: { xs: 1, sm: 1 },
                  }}
                >
                  {agent.role}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "12.5px", sm: "13px" },
                    color: "#65657A",
                    lineHeight: 1.7,
                    fontWeight: 400,
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {agent.description}
                </Typography>
              </Box>
            </NextLink>
          ))}

          {/* Dark CTA card */}
          <Box
            sx={{
              bgcolor: "#14122B",
              borderRadius: "16px",
              p: { xs: 2.5, sm: 3 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: isArabic ? "flex-end" : "flex-start",
              textAlign: isArabic ? "right" : "left",
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "17px" },
                fontWeight: 700,
                color: "#FFFFFF",
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {t("zaynAiTeam.ctaHeading")}
            </Typography>

            <NextLink
              href="/ai-agents"
              passHref
              legacyBehavior
            >
              <Button
                component="a"
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#14122B",
                  px: { xs: 3, sm: 3.5 },
                  py: { xs: 1, sm: 1.2 },
                  borderRadius: "50px",
                  textTransform: "none",
                  fontSize: { xs: "13px", sm: "14px" },
                  fontWeight: 600,
                  fontFamily: "var(--font-outfit)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "#fff",
                    transform: "scale(1.03)",
                  },
                }}
              >
                {t("zaynAiTeam.ctaButton")}
              </Button>
            </NextLink>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ZaynAiTeamSection;